import { type SVGProps, useEffect, useId, useMemo, useRef, useState } from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'

import { cn } from '@/lib/utils'

const NONE = '__shopora_searchable_none__'

function ChevronDown(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  )
}

function ChevronUp(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
    </svg>
  )
}

function Check(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  )
}

function SearchIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden {...props}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-4.35-4.35m1.6-5.4a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  )
}

export type SearchableSelectOption = {
  value: string
  label: string
  /** Extra text matched by search (e.g. bank code) */
  keywords?: string
}

export type SearchableSelectProps = {
  id?: string
  value: string
  onValueChange: (value: string) => void
  options: SearchableSelectOption[]
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  noneLabel?: string
  allowNone?: boolean
  disabled?: boolean
  loading?: boolean
  className?: string
  'aria-invalid'?: boolean
  'aria-label'?: string
}

/**
 * Radix select with an in-panel search field for long option lists (banks, etc.).
 */
export function SearchableSelect({
  id,
  value,
  onValueChange,
  options,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyMessage = 'No matches.',
  noneLabel = 'None',
  allowNone = true,
  disabled = false,
  loading = false,
  className,
  'aria-invalid': ariaInvalid,
  'aria-label': ariaLabel,
}: SearchableSelectProps) {
  const searchId = useId()
  const searchRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const selected = options.find((o) => o.value === value)
  const radixValue = !value || !selected ? (allowNone ? NONE : undefined) : value

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    let list = options
    if (q) {
      list = options.filter(
        (o) =>
          o.label.toLowerCase().includes(q) ||
          o.value.toLowerCase().includes(q) ||
          (o.keywords ?? '').toLowerCase().includes(q),
      )
    }
    // Keep the selected option visible while searching so Radix value stays valid.
    if (selected && q && !list.some((o) => o.value === selected.value)) {
      list = [selected, ...list]
    }
    return list
  }, [options, query, selected])

  useEffect(() => {
    if (!open) setQuery('')
    else requestAnimationFrame(() => searchRef.current?.focus())
  }, [open])

  return (
    <SelectPrimitive.Root
      value={radixValue}
      onValueChange={(v) => onValueChange(v === NONE ? '' : v)}
      open={open}
      onOpenChange={setOpen}
      disabled={disabled || loading}
    >
      <SelectPrimitive.Trigger
        id={id}
        aria-invalid={ariaInvalid}
        aria-label={ariaLabel}
        className={cn(
          'flex w-full cursor-pointer items-center justify-between gap-2 rounded-2xl border border-border bg-card px-4 py-2.5 text-left text-sm text-foreground shadow-sm outline-none transition-all',
          'hover:border-border focus:border-ring focus:ring-2 focus:ring-ring/30',
          'disabled:cursor-not-allowed disabled:opacity-60',
          'data-[placeholder]:text-muted-foreground [&_svg]:shrink-0',
          ariaInvalid && 'border-destructive ring-destructive/20',
          className,
        )}
      >
        <SelectPrimitive.Value placeholder={loading ? 'Loading…' : placeholder} />
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="size-4 opacity-50" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={6}
          className={cn(
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 z-50 max-h-[min(24rem,calc(100vh-4rem))] w-[var(--radix-select-trigger-width)] overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-md',
            'data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1',
          )}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <SelectPrimitive.ScrollUpButton className="flex cursor-default items-center justify-center py-1 text-muted-foreground">
            <ChevronUp className="size-4" />
          </SelectPrimitive.ScrollUpButton>

          <div
            className="border-b border-border bg-popover p-2"
            onPointerDown={(e) => e.stopPropagation()}
          >
            <label htmlFor={searchId} className="sr-only">
              {searchPlaceholder}
            </label>
            <div className="relative">
              <SearchIcon className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <input
                ref={searchRef}
                id={searchId}
                type="search"
                autoComplete="off"
                placeholder={searchPlaceholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                className="w-full rounded-lg border border-border bg-background py-2 pr-3 pl-9 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
              />
            </div>
          </div>

          <SelectPrimitive.Viewport className="max-h-[min(280px,calc(100vh-12rem))] overflow-y-auto p-1">
            {allowNone ? (
              <SelectPrimitive.Item
                value={NONE}
                className={cn(
                  'relative flex w-full cursor-default select-none items-center rounded-md py-2.5 pr-8 pl-2 text-sm outline-none',
                  'focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                )}
              >
                <span className="absolute right-2 flex size-3.5 items-center justify-center">
                  <SelectPrimitive.ItemIndicator>
                    <Check className="size-4" />
                  </SelectPrimitive.ItemIndicator>
                </span>
                <SelectPrimitive.ItemText>{noneLabel}</SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ) : null}

            {filtered.map((o) => (
              <SelectPrimitive.Item
                key={o.value}
                value={o.value}
                textValue={o.label}
                className={cn(
                  'relative flex w-full cursor-default select-none items-center rounded-md py-2.5 pr-8 pl-2 text-sm outline-none',
                  'focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                )}
              >
                <span className="absolute right-2 flex size-3.5 items-center justify-center">
                  <SelectPrimitive.ItemIndicator>
                    <Check className="size-4" />
                  </SelectPrimitive.ItemIndicator>
                </span>
                <SelectPrimitive.ItemText>{o.label}</SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ))}

            {filtered.length === 0 && options.length > 0 ? (
              <div className="px-2 py-4 text-center text-sm text-muted-foreground">{emptyMessage}</div>
            ) : null}
            {options.length === 0 && !loading ? (
              <div className="px-2 py-4 text-center text-sm text-muted-foreground">No options available.</div>
            ) : null}
          </SelectPrimitive.Viewport>

          <SelectPrimitive.ScrollDownButton className="flex cursor-default items-center justify-center py-1 text-muted-foreground">
            <ChevronDown className="size-4" />
          </SelectPrimitive.ScrollDownButton>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  )
}
